import {
  Box,
  Checkbox,
  Text,
  HStack,
  VStack,
  Icon,
  Button,
  Link,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { Briefcase, User, Building2, Calendar, Clock, Eye, FileText } from 'lucide-react';
import { Issue, ActionItem, User as UserType, Rock } from '../../types';
import {
  calculateDaysOpen,
  formatDate,
  formatDaysOpen,
  getIssueRockName,
  getIssueActionItems,
  getIssueAssignee,
} from '../issues.util';

interface IssuesCardViewProps {
  issues: Issue[];
  actionItems: ActionItem[];
  users: UserType[];
  rocks: Rock[];
  onResolve?: (issueId: string) => void;
  onView?: (issueId: string) => void;
}

/**
 * Issues Card View Component - Comfortable card layout
 */
export function IssuesCardView({
  issues,
  actionItems,
  users,
  rocks,
  onResolve,
  onView,
}: IssuesCardViewProps) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const textColorLight = useColorModeValue('gray.600', 'gray.400');
  const hoverBorder = useColorModeValue('gray.300', 'gray.500');

  if (issues.length === 0) {
    return (
      <Box py={8} textAlign="center">
        <Text color={textColorLight}>No issues to display</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {issues.map((issue) => {
        const issueActionItems = getIssueActionItems(issue.id, actionItems);
        const assignee = getIssueAssignee(issue, users);
        const rockName = getIssueRockName(issue, rocks);
        const daysOpen = calculateDaysOpen(issue.firstOpenedDate);
        const isResolved = issue.status === 'solved';

        return (
          <Box
            key={issue.id}
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
            _hover={{ borderColor: hoverBorder }}
          >
            <HStack spacing={3} align="start">
              <Checkbox mt={1} />
              <VStack align="stretch" flex={1} spacing={3}>
                {/* Title */}
                <Text fontWeight="bold" fontSize="md" color={textColor}>
                  {issue.name}
                </Text>

                {/* Metadata Row 1 */}
                <HStack spacing={4} flexWrap="wrap">
                  {issue.isCompany ? (
                    <HStack spacing={1.5}>
                      <Icon as={Briefcase} w={4} h={4} color={textColorLight} />
                      <Text fontSize="sm" color={textColor}>
                        Company-wide
                      </Text>
                    </HStack>
                  ) : rockName ? (
                    <HStack spacing={1.5}>
                      <Icon as={Eye} w={4} h={4} color="blue.500" />
                      <Text fontSize="sm" color={textColor}>
                        {rockName}
                      </Text>
                    </HStack>
                  ) : null}
                  {assignee && (
                    <HStack spacing={1.5}>
                      <Icon as={User} w={4} h={4} color={textColorLight} />
                      <Text fontSize="sm" color={textColor}>
                        Assigned: {assignee.name}
                      </Text>
                    </HStack>
                  )}
                  {assignee?.title && (
                    <HStack spacing={1.5}>
                      <Icon as={Building2} w={4} h={4} color={textColorLight} />
                      <Text fontSize="sm" color={textColor}>
                        {assignee.title}
                      </Text>
                    </HStack>
                  )}
                </HStack>

                {/* Metadata Row 2 */}
                <HStack spacing={4} flexWrap="wrap">
                  <HStack spacing={1.5}>
                    <Icon as={Calendar} w={4} h={4} color={textColorLight} />
                    <Text fontSize="sm" color={textColor}>
                      Created: {formatDate(issue.createdAt)}
                    </Text>
                  </HStack>
                  <HStack spacing={1.5}>
                    <Icon as={Clock} w={4} h={4} color={textColorLight} />
                    <Text fontSize="sm" color={textColor}>
                      Days open: {formatDaysOpen(daysOpen)}
                    </Text>
                  </HStack>
                </HStack>

                {/* Description */}
                <Text fontSize="sm" color={textColorLight}>
                  {issue.description}
                </Text>

                {/* Related Action Items */}
                {issueActionItems.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                      Related Action Items:
                    </Text>
                    {issueActionItems.map((actionItem) => (
                      <HStack key={actionItem.id} spacing={1.5} mb={1}>
                        <Icon as={FileText} w={3} h={3} color={textColorLight} />
                        <Link
                          fontSize="sm"
                          color="blue.500"
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {actionItem.description}
                        </Link>
                      </HStack>
                    ))}
                  </Box>
                )}

                <Divider />

                {/* Actions */}
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => onView?.(issue.id)}
                  >
                    View
                  </Button>
                  {!isResolved && onResolve && (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="green"
                      onClick={() => onResolve(issue.id)}
                    >
                      Resolve
                    </Button>
                  )}
                </HStack>
              </VStack>
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
}
